import { module } from 'modujs';
import { EAGER_FONTS } from '../app';
import { whenReady } from '../utils/fonts';
import BezierEasing from 'bezier-easing';
import { clamp, map } from '../utils/maths'

export default class RvMap extends module {
    constructor(m) {
        super(m);

        // UI
        this.$el = this.el
        this.$inner = this.$('inner')[0]
        this.$root = this.$('root')[0]
        this.$rootWrapper = this.$('rootWrapper')[0]

        // Binding
        this.onResizeEnd = this.onResizeEnd.bind(this)
        this.onFontsLoaded = this.onFontsLoaded.bind(this)

        // Easing
        this.easing = BezierEasing(0.165, 0.840, 0.440, 1.000)

        // CSS vars
        this.varGridMargin = 0
        this.varBorderRadius = 0

        // Global progress
        this.progress = 0

        // Progress ranges & values for each element
        this.stickyMask = {
            start: 0,
            end: 1,
            progress: 0,
            easedProgress: 0,
            value: 0
        }

        this.imageMask = {
            start: 0,
            end: 1,
            progress: 0,
            value: 0,
            roundBorders: {
                start: .8,
                end: 1,
                progress: 0,
                value: 0
            }
        }
    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        // Events
        this.bindEvents()
        whenReady(EAGER_FONTS).then((fonts) => this.onFontsLoaded(fonts));

        // Get Data
        this.getCssVars()
    }

    destroy() {
        super.destroy()
        this.unbindEvents()
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener('resizeEnd', this.onResizeEnd)
    }

    unbindEvents() {
        window.removeEventListener('resizeEnd', this.onResizeEnd)
    }

    ///////////////
    // Callbacks
    ///////////////
    onResizeEnd() {
        this.setImageMetrics()
        this.applyCover()
        this.getCssVars()

        requestAnimationFrame(() => {
            this.setProgressRanges()
        })
    }

    onScrollProgress(progress) {
        this.progress = progress
        this.computeProgress()
    }

    onFontsLoaded() {
        this.setImageMetrics()
        this.applyCover()
        this.getCssVars()

        requestAnimationFrame(() => {
            this.setProgressRanges()
        })
    }

    ///////////////
    // Methods
    ///////////////
    setImageMetrics() {
        // Image metrics
        this.imageMetrics = {
            oWidth: Number(window.innerWidth > window.innerHeight ? this.$root.dataset.imageLandscapeWidth : this.$root.dataset.imagePortraitWidth),
            oHeight: Number(window.innerWidth > window.innerHeight ? this.$root.dataset.imageLandscapeHeight : this.$root.dataset.imagePortraitHeight),
        }
    }

    setProgressRanges() {
        const windowHeight = window.innerHeight
        const moduleHeight = this.$el.getBoundingClientRect().height + windowHeight

        // Cascading progress ranges
        this.imageMask.end    = windowHeight / moduleHeight
        this.stickyMask.start = 1 - (windowHeight / moduleHeight)
        this.stickyMask.end   = 1 - .25 * (windowHeight / moduleHeight)
    }

    getCssVars() {
        const styles = getComputedStyle(document.documentElement)

        // Get raw css var value and remove ' '
        this.varGridMargin = styles.getPropertyValue('--grid-margin').trim()
        this.varBorderRadius = styles.getPropertyValue('--border-radius').trim()

        // Remove 'px' and convert it as int type
        this.varGridMargin = Number(this.varGridMargin.slice(0, -2))
        this.varBorderRadius = Number(this.varBorderRadius.slice(0, -3))
    }

    computeProgress() {
        // =========================================================
        // Mask image progress
        // =========================================================
        this.imageMask.progress = clamp(
            0,
            1,
            map(this.progress, this.imageMask.start, this.imageMask.end, 0, 1)
        )

        this.imageMask.roundBorders.progress = clamp(
            0,
            1,
            map(this.imageMask.progress, this.imageMask.roundBorders.start, this.imageMask.roundBorders.end, 0, 1)
        )

        // Calc percent value
        this.imageMask.value = 10 * (1 - this.imageMask.progress)
        this.imageMask.roundBorders.value = 20 * (1 - this.imageMask.roundBorders.progress)
        this.$el.style.clipPath = `inset(0 ${this.imageMask.value}% 0 ${this.imageMask.value}% round ${this.imageMask.roundBorders.value}px)`

        // =========================================================
        // Mask sticky container (end)
        // =========================================================
        // this.stickyMask.progress = clamp(
        //     0,
        //     1,
        //     map(this.progress, this.stickyMask.start, this.stickyMask.end, 0, 1)
        // )

        // Ease out progress
        // this.stickyMask.easedProgress = this.easing(this.stickyMask.progress)
        // this.$inner.style.clipPath = `inset(0 ${this.stickyMask.easedProgress * this.varGridMargin}px round ${this.stickyMask.easedProgress * this.varBorderRadius}rem)`
    }

    applyCover() {
        const imageRatio = this.imageMetrics.oWidth / this.imageMetrics.oHeight;

        const containerWidth = this.$rootWrapper.offsetWidth;
        const containerHeight = this.$rootWrapper.offsetHeight;
        const containerRatio = containerWidth / containerHeight;

        let newWidth, newHeight;

        if (imageRatio > containerRatio) {
            newHeight = containerHeight;
            newWidth = containerHeight * imageRatio;
        } else {
            newWidth = containerWidth;
            newHeight = containerWidth / imageRatio;
        }

        this.$root.style.width = `${newWidth}px`;
        this.$root.style.height = `${newHeight}px`;
        this.$inner.style.setProperty('--image-width-ratio', newWidth / this.imageMetrics.oWidth);
        this.$inner.style.setProperty('--image-height-ratio', newHeight / this.imageMetrics.oHeight);
    }
}
